export interface InvestorRegionEntry {
  slug: string;
  /**
   * Value matched against any element of `investors.regions`.
   * Exact case-insensitive match by default; substring match when
   * `useContains` is true (used for aggregate filters like "Europe").
   */
  regionValue: string;
  /** Whether to use ILIKE '%value%' substring match instead of exact match. */
  useContains?: boolean;
  label: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
}

export const INVESTOR_REGIONS: InvestorRegionEntry[] = [
  {
    slug: "europe",
    regionValue: "Europe",
    useContains: true,
    label: "Europe (all regions)",
    metaTitle: "European Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups across Western, Northern, Eastern and Southern Europe on CAPLINK.",
    h1: "European startup investors",
    intro:
      "Europe's venture ecosystem spans London, Berlin, Paris, Stockholm, Amsterdam, Madrid and beyond. Browse verified European investors on CAPLINK — covering Western, Northern, Eastern and Southern Europe.",
  },
  {
    slug: "western-europe",
    regionValue: "Western Europe",
    label: "Western Europe",
    metaTitle: "Western European Startup Investors | CAPLINK",
    metaDescription:
      "Find investors actively backing startups in Western Europe — including Germany, France, the UK, the Netherlands and Belgium.",
    h1: "Western European startup investors",
    intro:
      "Western Europe hosts the continent's largest VC hubs across London, Paris, Berlin, Amsterdam and Munich. Browse verified Western European investors on CAPLINK.",
  },
  {
    slug: "northern-europe",
    regionValue: "Northern Europe",
    label: "Northern Europe",
    metaTitle: "Northern European Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups in Northern Europe — Sweden, Denmark, Norway, Finland and the Baltics.",
    h1: "Northern European startup investors",
    intro:
      "Northern Europe — the Nordics and Baltics — punches well above its weight in venture, from Stockholm and Helsinki to Copenhagen, Oslo and Tallinn. Browse verified Northern European investors on CAPLINK.",
  },
  {
    slug: "eastern-europe",
    regionValue: "Eastern Europe",
    label: "Eastern Europe",
    metaTitle: "Eastern European Startup Investors | CAPLINK",
    metaDescription:
      "Find investors actively backing startups in Eastern Europe — Poland, Czechia, Hungary, Romania and the wider CEE region.",
    h1: "Eastern European startup investors",
    intro:
      "Eastern Europe is one of the fastest-growing venture ecosystems in EMEA, with hubs in Warsaw, Prague, Budapest, Bucharest and Vilnius. Browse verified Eastern European investors on CAPLINK.",
  },
  {
    slug: "southern-europe",
    regionValue: "Southern Europe",
    label: "Southern Europe",
    metaTitle: "Southern European Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups in Southern Europe — Spain, Italy, Portugal and Greece.",
    h1: "Southern European startup investors",
    intro:
      "Southern Europe's venture ecosystem spans Madrid, Barcelona, Milan, Lisbon and Athens. Browse verified Southern European investors on CAPLINK.",
  },
  {
    slug: "north-america",
    regionValue: "North America",
    label: "North America",
    metaTitle: "North American Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups in North America on CAPLINK. Filter by stage, sector and check size.",
    h1: "North American startup investors",
    intro:
      "The US and Canada are home to the world's deepest venture capital ecosystem. Browse verified North American investors on CAPLINK.",
  },
  {
    slug: "latin-america",
    regionValue: "Latin America",
    label: "Latin America",
    metaTitle: "Latin America Startup Investors | CAPLINK",
    metaDescription:
      "Find investors actively backing startups in Latin America — Brazil, Mexico, Colombia, Argentina and Chile.",
    h1: "Latin America startup investors",
    intro:
      "Latin America's startup ecosystem is growing across Brazil, Mexico, Colombia, Argentina and Chile. Browse verified LatAm investors on CAPLINK.",
  },
  {
    slug: "central-america-caribbean",
    regionValue: "Central America & Caribbean",
    label: "Central America & Caribbean",
    metaTitle: "Central America & Caribbean Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups across Central America and the Caribbean on CAPLINK.",
    h1: "Central America & Caribbean startup investors",
    intro:
      "From Costa Rica and Panama to the Dominican Republic, Jamaica and Puerto Rico, Central America and the Caribbean host a growing venture scene. Browse verified investors on CAPLINK.",
  },
  {
    slug: "middle-east",
    regionValue: "Middle East",
    label: "Middle East",
    metaTitle: "Middle East Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups in the Middle East — UAE, Saudi Arabia, Israel and beyond.",
    h1: "Middle East startup investors",
    intro:
      "The Middle East's venture ecosystem is expanding rapidly, led by the UAE, Saudi Arabia and Israel. Browse verified Middle East investors on CAPLINK.",
  },
  {
    slug: "asia-pacific",
    regionValue: "Asia-Pacific",
    label: "Asia-Pacific",
    metaTitle: "Asia-Pacific Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups in the Asia-Pacific region — Australia, New Zealand and the wider APAC ecosystem.",
    h1: "Asia-Pacific startup investors",
    intro:
      "Asia-Pacific spans Sydney, Auckland, Singapore, Tokyo, Seoul and beyond. Browse verified APAC investors on CAPLINK.",
  },
  {
    slug: "southeast-asia",
    regionValue: "Southeast Asia",
    label: "Southeast Asia",
    metaTitle: "Southeast Asia Startup Investors | CAPLINK",
    metaDescription:
      "Find investors actively backing startups in Southeast Asia — Singapore, Indonesia, Vietnam, Malaysia, Thailand and the Philippines.",
    h1: "Southeast Asia startup investors",
    intro:
      "Southeast Asia's startup ecosystem is growing rapidly across Singapore, Jakarta, Ho Chi Minh City, Kuala Lumpur, Bangkok and Manila. Browse verified SEA investors on CAPLINK.",
  },
  {
    slug: "east-asia",
    regionValue: "East Asia",
    label: "East Asia",
    metaTitle: "East Asia Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups in East Asia — China, Japan, South Korea, Taiwan and Hong Kong.",
    h1: "East Asia startup investors",
    intro:
      "East Asia hosts some of the world's largest venture markets — Beijing, Shanghai, Tokyo, Seoul, Taipei and Hong Kong. Browse verified East Asian investors on CAPLINK.",
  },
  {
    slug: "south-asia",
    regionValue: "South Asia",
    label: "South Asia",
    metaTitle: "South Asia Startup Investors | CAPLINK",
    metaDescription:
      "Find investors actively backing startups in South Asia — India, Pakistan, Bangladesh and Sri Lanka.",
    h1: "South Asia startup investors",
    intro:
      "South Asia's venture market is led by India's hubs in Bengaluru, Mumbai and Delhi, with growing ecosystems across Pakistan, Bangladesh and Sri Lanka. Browse verified South Asian investors on CAPLINK.",
  },
  {
    slug: "africa",
    regionValue: "Africa",
    label: "Africa",
    metaTitle: "African Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors actively backing startups in Africa — Nigeria, Kenya, South Africa, Egypt and beyond.",
    h1: "African startup investors",
    intro:
      "Africa's startup ecosystem is growing rapidly across Lagos, Nairobi, Cape Town, Cairo and beyond. Browse verified African investors on CAPLINK.",
  },
  {
    slug: "russia-central-asia",
    regionValue: "Russia & Central Asia",
    label: "Russia & Central Asia",
    metaTitle: "Russia & Central Asia Startup Investors | CAPLINK",
    metaDescription:
      "Browse investors active across Russia and Central Asia — including Kazakhstan, Uzbekistan and the wider CIS region.",
    h1: "Russia & Central Asia startup investors",
    intro:
      "Russia and Central Asia — including Kazakhstan, Uzbekistan and the wider CIS — host an emerging venture ecosystem. Browse verified investors on CAPLINK.",
  },
];

export const investorRegionFromSlug = (slug: string | undefined): InvestorRegionEntry | undefined =>
  INVESTOR_REGIONS.find((r) => r.slug === slug);
